// src/components/header/Header.jsx
// Updated: Admin only sees Dashboard, Settings, Logout in dropdown

import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./header.css";
import logo from "../../assets/logo3.png";
import GoodbyePopup from '../auth/GoodbyePopup';

const Header = ({ isAuthenticated: propIsAuthenticated, user: propUser, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [Toggle, showMenu] = useState(false);
  const [activeNav, setActiveNav] = useState("#home");
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Enhanced authentication states
  const [user, setUser] = useState(propUser || null);
  const [isAuthenticated, setIsAuthenticated] = useState(propIsAuthenticated || false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showGoodbye, setShowGoodbye] = useState(false);
  const dropdownRef = useRef(null);

  // Update state when props change
  useEffect(() => {
    setIsAuthenticated(propIsAuthenticated);
    setUser(propUser);
  }, [propIsAuthenticated, propUser]);

  // Check authentication on mount (if not passed as props)
  useEffect(() => {
    if (!propIsAuthenticated) {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const userData = localStorage.getItem('userInfo') || localStorage.getItem('user');
      
      if (token && userData) {
        try {
          setIsAuthenticated(true);
          setUser(JSON.parse(userData));
        } catch (e) {
          console.error('Error parsing user data');
        }
      }
    }
  }, [propIsAuthenticated]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      setScrollProgress((scrollPosition / windowHeight) * 100);
      setScrolled(scrollPosition >= 80);
      
      const header = document.querySelector(".header");
      if (header) {
        if (scrollPosition >= 80) {
          header.classList.add("scroll-header");
        } else {
          header.classList.remove("scroll-header");
        }
      }
    };
    
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear authentication data
  const handleAuthClear = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userInfo');
    sessionStorage.removeItem('authToken');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Nav click handler
  const handleNavClick = (nav) => {
    setActiveNav(nav);
    showMenu(false);
    
    if (nav.startsWith("#")) {
      const element = document.querySelector(nav);
      if (element) {
        element.scrollIntoView({ 
          behavior: "smooth", 
          block: "start" 
        });
      }
    }
  };

  // Enhanced logout with goodbye popup
  const handleLogoutClick = async () => {
    // Get user name for popup
    const userName = user?.profile?.firstName || user?.firstName || 'Friend';
    
    // Show goodbye popup
    setShowGoodbye(true);
    
    // Clear auth data
    if (onLogout) {
      onLogout();
    } else {
      handleAuthClear();
    }
    
    // Close dropdowns
    setShowProfileDropdown(false);
    showMenu(false);

    // Redirect after popup animation (2 seconds)
    setTimeout(() => {
      navigate('/');
      setShowGoodbye(false);
    }, 2000);
  };

  // Profile dropdown items - DIFFERENT FOR ADMIN VS CLIENT
  const profileMenuItems = user?.role === 'admin' 
    ? [
        // ADMIN MENU - Only Dashboard, Settings, Logout
        { 
          label: 'Dashboard', 
          icon: 'bx-grid-alt', 
          onClick: () => {
            navigate('/admin');
            setShowProfileDropdown(false);
          }
        },
        { 
          label: 'Settings', 
          icon: 'bx-cog', 
          onClick: () => {
            navigate('/admin');
            setShowProfileDropdown(false);
          }
        },
        { type: 'divider' },
        { 
          label: 'Logout', 
          icon: 'bx-log-out', 
          onClick: handleLogoutClick,
          className: 'logout-item'
        }
      ]
    : [
        // CLIENT MENU - Dashboard, Projects, Logout only
        { 
          label: 'Dashboard', 
          icon: 'bx-grid-alt', 
          onClick: () => {
            navigate('/dashboard');
            setShowProfileDropdown(false);
          }
        },
        { 
          label: 'Services', 
          icon: 'bx-briefcase-alt', 
          onClick: () => {
            navigate('/dashboard?tab=customize');
            setShowProfileDropdown(false);
          }
        },
        { 
          label: 'Projects', 
          icon: 'bx-folder', 
          onClick: () => {
            navigate('/dashboard?tab=projects');
            setShowProfileDropdown(false);
          }
        },
        { type: 'divider' },
        { 
          label: 'Logout', 
          icon: 'bx-log-out', 
          onClick: handleLogoutClick,
          className: 'logout-item'
        }
      ];

  return (
    <>
      <header className={`header ${scrolled ? 'scroll-header' : ''}`}>
        <div
          className="scroll-progress"
          style={{ width: `${scrollProgress}%` }}
        ></div>
        <nav className="nav container">
          <Link to="/" className="nav__logo">
            <img
              src={logo}
              alt="SpinoSoftBits"
              className="nav__logo-img"
            />
          </Link>

          {/* Toggle button */}
          <button 
            className={Toggle ? "nav__toggle toggle-active" : "nav__toggle"} 
            onClick={() => showMenu(!Toggle)}
            aria-label="Open navigation menu"
            aria-expanded={Toggle ? "true" : "false"}
          >
            <i className="bx bx-menu-alt-right nav__toggle-icon"></i>
            <span className="nav__toggle-text">Menu</span>
          </button>

          {/* Menu */}
          <div className={Toggle ? "nav__menu show-menu" : "nav__menu"}>
            <i 
              className="uil uil-times nav__close" 
              onClick={() => showMenu(false)}
            ></i>
            
            <ul className="nav__list">
              <li className="nav__item">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveNav("#home");
                    showMenu(false);
                    if (location.pathname !== "/") {
                      navigate("/");
                      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
                    } else {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className="nav__link active-link nav__link--primary"
                >
                  <i className="uil uil-estate nav__icon"></i> Home
                </a>
              </li>
              {!isAuthenticated && (
                <li className="nav__item">
                  <a
                    href="#services"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick("#services");
                    }}
                    className={activeNav === "#services" ? "nav__link active-link" : "nav__link"}
                  >
                    <i className="uil uil-briefcase-alt nav__icon"></i> Services
                  </a>
                </li>
              )}
              <li className="nav__item">
                <a
                  href="#about"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("#about");
                  }}
                  className={activeNav === "#about" ? "nav__link active-link" : "nav__link"}
                >
                  <i className="uil uil-user nav__icon"></i> About
                </a>
              </li>
              <li className="nav__item">
                <a
                  href="#portfolio"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("#portfolio");
                  }}
                  className={activeNav === "#portfolio" ? "nav__link active-link" : "nav__link"}
                >
                  <i className="bx bx-layer nav__icon"></i> Apps
                </a>
              </li>
              <li className="nav__item">
                <a
                  href="#blog"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("#blog");
                  }}
                  className={activeNav === "#blog" ? "nav__link active-link" : "nav__link"}
                >
                  <i className="uil uil-document-layout-left nav__icon"></i> Blog
                </a>
              </li>
              <li className="nav__item">
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("#contact");
                  }}
                  className={activeNav === "#contact" ? "nav__link active-link" : "nav__link"}
                >
                  <i className="uil uil-message nav__icon"></i> Contact
                </a>
              </li>
            </ul>

            {/* Auth buttons / Profile dropdown */}
            <div className="nav__btns">
              {isAuthenticated ? (
                <>
                  {/* Desktop: Profile dropdown */}
                  <div className="nav__profile-desktop" ref={dropdownRef}>
                    <button 
                      className="nav__profile-btn"
                      onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                      aria-expanded={showProfileDropdown}
                    >
                      <div className="nav__avatar">
                        {user?.profile?.avatar ? (
                          <img src={user.profile.avatar} alt={user.profile.firstName} />
                        ) : (
                          <span>{(user?.profile?.firstName || user?.firstName || 'U')[0]}</span>
                        )}
                      </div>
                      <span className="nav__profile-name">
                        {user?.profile?.firstName || user?.firstName || 'User'}
                      </span>
                      <i className={`bx bx-chevron-down nav__profile-arrow ${showProfileDropdown ? 'rotate' : ''}`}></i>
                    </button>

                    {/* Dropdown menu */}
                    {showProfileDropdown && (
                      <div className="profile-dropdown">
                        <div className="profile-header">
                          <div className="profile-avatar">
                            {user?.profile?.avatar ? (
                              <img src={user.profile.avatar} alt={user.profile.firstName} />
                            ) : (
                              <span>{(user?.profile?.firstName || user?.firstName || 'U')[0]}</span>
                            )}
                          </div>
                          <div className="profile-info">
                            <h4>
                              {user?.profile?.firstName || user?.firstName} {user?.profile?.lastName || user?.lastName || ''}
                            </h4>
                            <p>{user?.email}</p>
                            {user?.role === 'admin' && (
                              <span className="admin-tag">Admin</span>
                            )}
                          </div>
                        </div>
                        <div className="profile-menu">
                          {profileMenuItems.map((item, index) => (
                            item.type === 'divider' ? (
                              <div key={index} className="profile-menu-divider"></div>
                            ) : (
                              <button
                                key={index}
                                className={`profile-menu-item ${item.className || ''}`}
                                onClick={item.onClick}
                              >
                                <i className={`bx ${item.icon}`}></i>
                                <span>{item.label}</span>
                              </button>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mobile: Simple welcome and logout */}
                  <div className="nav__profile-mobile">
                    <span className="nav__welcome">
                      Hey, {user?.profile?.firstName || user?.firstName || user?.name || "User"} 👋
                    </span>
                    <button className="nav__login" onClick={handleLogoutClick}>
                      Sign out
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav__login">
                    Sign In
                  </Link>
                  <Link to="/register" className="nav__signup">
                    Get Started
                    <i className="uil uil-arrow-right nav__signup-icon"></i>
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Goodbye Popup */}
      <GoodbyePopup 
        show={showGoodbye} 
        onClose={() => setShowGoodbye(false)}
        userName={user?.profile?.firstName || user?.firstName || 'Friend'}
        type="goodbye"
      />
    </>
  );
};

export default Header;